export type CharacterAppearance = 'boy' | 'girl';
export type CharacterPresetId = 'boy-basic' | 'girl-basic';

export interface CharacterPreset {
  id: CharacterPresetId;
  appearance: CharacterAppearance;
  appearanceLabel: string;
  outfitLabel: string;
  imageUrl: string;
}

export const DEFAULT_CHARACTER_PRESET: CharacterPresetId = 'boy-basic';

export const CHARACTER_PRESETS: CharacterPreset[] = [
  {
    id: 'boy-basic',
    appearance: 'boy',
    appearanceLabel: '남학생',
    outfitLabel: '아이보리 맨투맨과 청바지',
    imageUrl: '/images/student-profile/animation/SD_DRESSUP/boy/outfit-ivory.png'
  },
  {
    id: 'girl-basic',
    appearance: 'girl',
    appearanceLabel: '여학생',
    outfitLabel: '아이보리 맨투맨과 청바지',
    imageUrl: '/images/student-profile/animation/SD_DRESSUP/girl/outfit-ivory.png'
  }
];

/** 이전 프리셋 선택을 새 기본형으로 자연스럽게 이전합니다. */
function normalizeLegacyPreset(id?: string | null): CharacterPresetId {
  if (id?.startsWith('girl-')) return 'girl-basic';
  return 'boy-basic';
}

export function getCharacterPreset(id?: string | null): CharacterPreset {
  const normalizedId = normalizeLegacyPreset(id);
  return CHARACTER_PRESETS.find((preset) => preset.id === normalizedId) || CHARACTER_PRESETS[0];
}

export function findCharacterPreset(appearance: CharacterAppearance): CharacterPreset {
  return CHARACTER_PRESETS.find((preset) => preset.appearance === appearance) || CHARACTER_PRESETS[0];
}

export type CharacterShopOutfitId =
  | 'orange-star-jacket'
  | 'orange-hoodie'
  | 'school-look'
  | 'denim-casual'
  | 'pe-tracksuit'
  | 'winter-duffle'
  | 'denim-overalls'
  | 'monochrome-cape'
  | 'halloween-night'
  | 'beginner-wizard'
  | 'forest-explorer'
  | 'pastel-varsity'
  | 'black-layered'
  | 'navy-cardigan'
  | 'taupe-knit-vest';

export interface CharacterShopOutfit {
  id: CharacterShopOutfitId;
  name: string;
  description: string;
  boyImageUrl?: string;
  girlImageUrl: string;
  /** 교사 상점과 학생 상점 카드에 사용하는 남녀 공용 썸네일 */
  shopImageUrl?: string;
}

export const CHARACTER_SHOP_OUTFITS: CharacterShopOutfit[] = [
  {
    id: 'orange-star-jacket',
    name: '오렌지 스타 재킷',
    description: '얼굴과 머리는 그대로, 상의만 바뀌는 SD 재킷입니다. 기본 청바지와 운동화를 함께 착용합니다.',
    boyImageUrl: '/images/student-profile/animation/SD_DRESSUP/boy/outfit-orange.png',
    girlImageUrl: '/images/student-profile/animation/SD_DRESSUP/girl/outfit-orange.png',
    shopImageUrl: '/images/student-profile/animation/SD_DRESSUP/orange-star-jacket-preview.png'
  },
  {
    id: 'orange-hoodie',
    name: '주황 후드 세트',
    description: '포근한 주황 후드와 청바지로 구성된 캐릭터 복장입니다.',
    boyImageUrl: '/images/student-profile/animation/SD_DRESSUP/boy/outfit-orange-hoodie.png',
    girlImageUrl: '/images/student-profile/animation/SD_DRESSUP/girl/outfit-orange-hoodie.png',
    shopImageUrl: '/images/student-profile/animation/SD_DRESSUP/orange-hoodie-preview.png'
  },
  {
    id: 'school-look',
    name: '단정한 교복 세트',
    description: '셔츠와 네이비 조끼를 매치한 단정한 캐릭터 복장입니다.',
    boyImageUrl: '/images/student-profile/animation/SD_DRESSUP/boy/outfit-school-look.png',
    girlImageUrl: '/images/student-profile/animation/SD_DRESSUP/girl/outfit-school-look.png',
    shopImageUrl: '/images/student-profile/animation/SD_DRESSUP/school-look-preview.png'
  },
  {
    id: 'denim-casual',
    name: '데님 캐주얼 세트',
    description: '데님 재킷과 줄무늬 티셔츠를 매치한 산뜻한 캐주얼 복장입니다.',
    boyImageUrl: '/images/student-profile/animation/SD_DRESSUP/boy/outfit-denim-casual.png',
    girlImageUrl: '/images/student-profile/animation/SD_DRESSUP/girl/outfit-denim-casual.png',
    shopImageUrl: '/images/student-profile/animation/SD_DRESSUP/denim-casual-preview.png'
  },
  {
    id: 'pe-tracksuit',
    name: '학교 체육복 세트',
    description: '활동하기 편한 청록색 집업과 트레이닝 바지 세트입니다.',
    boyImageUrl: '/images/student-profile/animation/SD_DRESSUP/boy/outfit-pe-tracksuit.png',
    girlImageUrl: '/images/student-profile/animation/SD_DRESSUP/girl/outfit-pe-tracksuit.png',
    shopImageUrl: '/images/student-profile/animation/SD_DRESSUP/pe-tracksuit-preview.png'
  },
  {
    id: 'winter-duffle',
    name: '겨울 더플코트 세트',
    description: '따뜻한 더플코트와 빨간 목도리를 함께 입는 겨울 복장입니다.',
    boyImageUrl: '/images/student-profile/animation/SD_DRESSUP/boy/outfit-winter-duffle.png',
    girlImageUrl: '/images/student-profile/animation/SD_DRESSUP/girl/outfit-winter-duffle.png',
    shopImageUrl: '/images/student-profile/animation/SD_DRESSUP/winter-duffle-preview.png'
  },
  {
    id: 'denim-overalls',
    name: '데님 멜빵 세트',
    description: '노란 티셔츠와 데님 멜빵바지를 매치한 귀여운 복장입니다.',
    boyImageUrl: '/images/student-profile/animation/SD_DRESSUP/boy/outfit-denim-overalls.png',
    girlImageUrl: '/images/student-profile/animation/SD_DRESSUP/girl/outfit-denim-overalls.png',
    shopImageUrl: '/images/student-profile/animation/SD_DRESSUP/denim-overalls-preview.png'
  },
  {
    id: 'monochrome-cape',
    name: '달빛 흑백 케이프 세트',
    description: '흑백 후드 케이프와 매듭 장식이 돋보이는 신비로운 복장입니다.',
    boyImageUrl: '/images/student-profile/animation/SD_DRESSUP/boy/outfit-monochrome-cape.png',
    girlImageUrl: '/images/student-profile/animation/SD_DRESSUP/girl/outfit-monochrome-cape.png',
    shopImageUrl: '/images/student-profile/animation/SD_DRESSUP/monochrome-cape-preview.png'
  },
  {
    id: 'halloween-night',
    name: '할로윈 밤 축제 세트',
    description: '꼬마 뱀파이어와 꼬마 마녀로 꾸미는 할로윈 이벤트 한정 복장입니다.',
    boyImageUrl: '/images/student-profile/animation/SD_DRESSUP/boy/outfit-halloween-night.png',
    girlImageUrl: '/images/student-profile/animation/SD_DRESSUP/girl/outfit-halloween-night.png',
    shopImageUrl: '/images/student-profile/animation/SD_DRESSUP/halloween-night-preview.png'
  },
  ...([
    ['beginner-wizard', '초급 마법사 세트', '별 장식 모자와 남색 망토를 입는 마법사 복장입니다.', false],
    ['forest-explorer', '숲속 탐험가 세트', '카키 재킷과 노란 스카프, 카고 바지를 매치한 탐험가 복장입니다.', false],
    ['pastel-varsity', '파스텔 야구점퍼 세트', '민트색 야구점퍼와 크림색 소매가 어우러지는 캐주얼 복장입니다.', false],
    ['black-layered', '블랙 레이어드 캐주얼 세트', '검정 집업에 회색 맨투맨과 흰 티셔츠를 겹쳐 입는 복장입니다.', false],
    ['navy-cardigan', '네이비 카디건 세트 (여학생 전용)', '여학생 캐릭터 전용입니다. 남색 카디건과 하늘색 플리츠 스커트를 입습니다.', true],
    ['taupe-knit-vest', '베이지 니트 조끼 세트 (여학생 전용)', '여학생 캐릭터 전용입니다. 베이지 니트 조끼와 흰 셔츠, 검정 와이드 팬츠를 입습니다.', true]
  ] as const).map(([id, name, description, girlOnly]) => ({
    id,
    name,
    description,
    ...(girlOnly ? {} : { boyImageUrl: `/images/student-profile/animation/SD_DRESSUP/boy/outfit-${id}.png` }),
    girlImageUrl: `/images/student-profile/animation/SD_DRESSUP/girl/outfit-${id}.png`,
    shopImageUrl: `/images/student-profile/animation/SD_DRESSUP/${id}-preview.png`
  }))
];

export function getCharacterShopOutfit(id?: string | null): CharacterShopOutfit | undefined {
  return CHARACTER_SHOP_OUTFITS.find((outfit) => outfit.id === id);
}

export function getCharacterShopOutfitImage(
  id: string | null | undefined,
  appearance: CharacterAppearance
): string | undefined {
  const outfit = getCharacterShopOutfit(id);
  return outfit ? (appearance === 'girl' ? outfit.girlImageUrl : outfit.boyImageUrl) : undefined;
}

/** Legacy/custom items remain usable; catalog outfits require an image for this avatar. */
export function canWearCharacterShopOutfit(id: string | null | undefined, appearance: CharacterAppearance): boolean {
  return !getCharacterShopOutfit(id) || Boolean(getCharacterShopOutfitImage(id, appearance));
}

/** 학생 홈·옷장·마이홈에서 현재 착용 중인 완성 캐릭터 PNG를 고릅니다. */
export function getEquippedCharacterImageUrl(
  presetId: string | null | undefined,
  equippedBaseId: string | null | undefined,
  inventory: string[],
  storeItems: Record<string, { imageUrl?: string; characterOutfitId?: string }>
): string {
  const preset = getCharacterPreset(presetId);
  if (equippedBaseId && inventory.includes(equippedBaseId) && storeItems[equippedBaseId]) {
    const item = storeItems[equippedBaseId];
    if (!canWearCharacterShopOutfit(item.characterOutfitId, preset.appearance)) return preset.imageUrl;
    return getCharacterShopOutfitImage(item.characterOutfitId, preset.appearance) || item.imageUrl || preset.imageUrl;
  }
  return preset.imageUrl;
}
/** Only registered SD outfits can be split. Legacy/custom full outfits remain untouched. */
export function getCharacterImageLayers(imageUrl?: string): string[] {
  if (!imageUrl) return [];
  for (const appearance of ['boy', 'girl'] as const) {
    const root = `/images/student-profile/animation/SD_DRESSUP/${appearance}`;
    for (const top of ['ivory', 'orange'] as const) {
      if (imageUrl === `${root}/outfit-${top}.png`) {
        return [`${root}/fixed-character.png`, `${root}/top-${top}.png`];
      }
    }
    const outfit = CHARACTER_SHOP_OUTFITS.find((item) =>
      (appearance === 'boy' ? item.boyImageUrl : item.girlImageUrl) === imageUrl
    );
    if (outfit) {
      const hasHat = outfit.id === 'beginner-wizard' || (outfit.id === 'halloween-night' && appearance === 'girl');
      return [`${root}/outfit-layer-${outfit.id}.png`, `${root}/${hasHat ? 'fixed-face' : 'fixed-head'}.png`];
    }
  }
  return [imageUrl];
}
